import { Fragment, useEffect, useState, type FormEvent } from 'react';
import { systemOrgs, type OrganisationSummary } from './system.api';
import {
  Panel, ErrorNote, Field, inputClass, buttonClass, secondaryButtonClass, tableHeadClass,
} from './SystemLayout';
import { RolesPanel } from './RolesPanel';

/** Tenants: list, provision, suspend. */
export default function OrganisationsPage() {
  const [rows, setRows] = useState<OrganisationSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  /** Which organisation's role drawer is open. One at a time: two open
   *  role tables on one screen is noise, not power. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      setRows(await systemOrgs.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load organisations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function suspend(org: OrganisationSummary) {
    const suspending = org.status === 'ACTIVE';
    const reason = suspending
      ? window.prompt(
          `Suspend "${org.name}"?\n\nIts ${org.activeUserCount} active people will not be able to sign in. `
          + 'No data is deleted and this can be undone.\n\nReason (recorded in the audit):',
        )
      : null;
    // prompt() returns null on Cancel - only an empty string means "no reason".
    if (suspending && reason === null) return;

    setError(null);
    try {
      const updated = await systemOrgs.setSuspended(org.id, suspending, reason);
      setRows((current) => current.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the suspension');
    }
  }

  return (
    <div className="space-y-5">
      <ErrorNote message={error} />

      <Panel
        title={`Organisations${rows.length > 0 ? ` (${rows.length})` : ''}`}
        action={
          <button type="button" onClick={() => setCreating((v) => !v)} className={buttonClass}>
            {creating ? 'Cancel' : 'New organisation'}
          </button>
        }
      >
        {creating && <CreateForm onDone={() => { setCreating(false); void load(); }} />}

        {loading ? (
          <p className="text-sm text-slate-400">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400">No organisations yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className={tableHeadClass}>
                {/* Explicit widths so the columns do not reflow as rows
                    change - "PREFIXPEOPLE" ran together when a wide cell
                    squeezed them. Name takes the slack. */}
                <th className="py-2 font-medium">Name</th>
                <th className="w-28 py-2 font-medium">Prefix</th>
                <th className="w-36 py-2 font-medium">People</th>
                <th className="w-28 py-2 font-medium">Status</th>
                <th className="w-52 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((org) => (
                <Fragment key={org.id}>
                  <tr className="border-b border-slate-100 last:border-0">
                    {/*
                      The edit form is NOT in this cell. Putting it here
                      forced the Name column wide enough to squash the
                      headers together ("PREFIXPEOPLE") and truncate the
                      timezone. It lives in its own full-width row below,
                      the same shape as the roles drawer.
                    */}
                    <td className="py-2.5">
                      <span className="font-medium text-slate-900">{org.name}</span>
                      <span className="ml-2 text-xs text-slate-500">
                        {org.timezone} · {org.locale}
                      </span>
                      {/* Not a new column - the header comment above explains
                          why another cell reflows this table. */}
                      <span className="mt-0.5 block text-xs text-slate-400">
                        Created {new Date(org.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="py-2.5 text-slate-500">{org.taskKeyPrefix}</td>
                    {/* Counts only - the console never sees who these people are. */}
                    <td className="py-2.5 text-slate-500">
                      {org.activeUserCount} active
                      <span className="text-slate-400"> / {org.userCount}</span>
                    </td>
                    <td className="py-2.5">
                      <span className={
                        org.status === 'ACTIVE'
                          ? 'rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700'
                          : 'rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700'
                      }>
                        {org.status === 'ACTIVE' ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-2.5 text-right">
                      <button type="button"
                        onClick={() => { setOpenId((id) => (id === org.id ? null : org.id)); setEditing(null); }}
                        className="text-xs font-medium text-brand-600 underline-offset-2 hover:underline">
                        {openId === org.id ? 'Hide roles' : 'Roles'}
                      </button>
                      <button type="button"
                        onClick={() => {
                          setEditing((id) => (id === org.id ? null : org.id));
                          setOpenId(null);
                        }}
                        className={`ml-3 text-xs font-medium underline-offset-2 hover:underline ${
                          editing === org.id ? 'text-slate-900' : 'text-brand-600'
                        }`}>
                        {editing === org.id ? 'Close' : 'Edit'}
                      </button>
                      <button type="button" onClick={() => void suspend(org)}
                        className="ml-3 text-xs font-medium text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline">
                        {org.status === 'ACTIVE' ? 'Suspend' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                  {editing === org.id && (
                    <tr className="border-b border-slate-100">
                      <td colSpan={5} className="bg-slate-50 px-4 py-4">
                        <EditForm
                          org={org}
                          onSaved={(updated) => {
                            setRows((current) => current.map((r) => (r.id === updated.id ? updated : r)));
                            setEditing(null);
                          }}
                          onError={setError}
                          onCancel={() => setEditing(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {openId === org.id && (
                    <tr className="border-b border-slate-100">
                      <td colSpan={5} className="bg-slate-50 px-4 py-4">
                        <RolesPanel orgId={org.id} orgName={org.name} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

function CreateForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [locale, setLocale] = useState('en-IN');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await systemOrgs.create({
        name, taskKeyPrefix: prefix.toUpperCase(), timezone, locale,
        admin: { email: adminEmail, displayName: adminName, password: adminPassword },
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the organisation');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}
          className="mb-6 space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <ErrorNote message={error} />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Organisation name">
          <input required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
        </Field>
        <Field label='Task key prefix - e.g. "SC-"'>
          <input required value={prefix} placeholder="SC-" maxLength={8}
                 onChange={(e) => setPrefix(e.target.value.toUpperCase())} className={inputClass} />
          {/* Editable later (Edit > Task key prefix), but never retroactive:
              tasks already minted keep the key they were given. */}
          <span className="mt-1 block text-[11px] text-slate-500">
            Capital letters ending in a hyphen - <span className="font-mono">SC-</span>{' '}
            or <span className="font-mono">CG-CH-</span>. Up to 8 characters.
          </span>
        </Field>
        <Field label="Timezone">
          <input required value={timezone} onChange={(e) => setTimezone(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Locale">
          <input required value={locale} onChange={(e) => setLocale(e.target.value)} className={inputClass} />
        </Field>
      </div>

      <div className="rounded-xl border border-slate-200 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          First Super Admin
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Email">
            <input required type="email" value={adminEmail}
                   onChange={(e) => setAdminEmail(e.target.value)} className={inputClass} />
          </Field>
          <Field label="Display name">
            <input required value={adminName}
                   onChange={(e) => setAdminName(e.target.value)} className={inputClass} />
          </Field>
          <Field label="Password" hint="At least 8 characters.">
            {/* minLength matches the API's own rule, so the browser catches a
                short password before a round-trip returns a 422 for it. */}
            <input required type="password" minLength={8} value={adminPassword}
                   onChange={(e) => setAdminPassword(e.target.value)} className={inputClass} />
          </Field>
        </div>
        {/* An existing account is reused, not refused - one person can run
            more than one tenant on a single password. */}
        <p className="mt-2 text-[11px] text-slate-500">
          If this email already has an account, it is linked to the new organisation
          and keeps its existing password - the one above is then ignored.
        </p>
      </div>

      <button type="submit" disabled={busy} className={buttonClass}>
        {busy ? 'Creating...' : 'Create organisation'}
      </button>
    </form>
  );
}


/**
 * Rename a tenant, or correct its timezone or locale.
 *
 * The task key prefix is NOT here. It is baked into every task key the
 * organisation has ever minted - CG-1, CG-2 - and those strings are
 * printed on PDFs and quoted in email, so changing it would leave two
 * prefixes in circulation. The API refuses it outright.
 */
function EditForm({ org, onSaved, onError, onCancel }: {
  org: OrganisationSummary;
  onSaved: (org: OrganisationSummary) => void;
  onError: (message: string | null) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(org.name);
  const [timezone, setTimezone] = useState(org.timezone);
  const [locale, setLocale] = useState(org.locale);
  const [prefix, setPrefix] = useState(org.taskKeyPrefix);
  const [busy, setBusy] = useState(false);

  const prefixChanged = prefix.trim().toUpperCase() !== org.taskKeyPrefix;

  async function submit(event: FormEvent) {
    event.preventDefault();

    /*
     * A prefix change is the one edit on this form that cannot be undone
     * by editing it back - tasks minted in between keep the new prefix
     * for ever. So it is confirmed, and the confirmation says exactly
     * what will and will not change rather than asking "are you sure?".
     */
    if (prefixChanged && !window.confirm(
      `Change the task key prefix from ${org.taskKeyPrefix} to ${prefix.trim().toUpperCase()}?\n\n`
      + `This is NOT retroactive:\n`
      + `  • every existing task keeps its current key (${org.taskKeyPrefix}1, ${org.taskKeyPrefix}2, …)\n`
      + `  • only NEW tasks get ${prefix.trim().toUpperCase()}\n`
      + `  • the numbering continues, so you will see ${org.taskKeyPrefix}41 then ${prefix.trim().toUpperCase()}42\n`
      + `  • keys already on sent PDFs and emails cannot change\n`
      + `  • future CG Sign serial numbers change too\n\n`
      + `Editing it back later will not undo it for tasks created in between.`,
    )) return;

    setBusy(true);
    onError(null);
    try {
      onSaved(await systemOrgs.update(org.id, {
        name, timezone, locale,
        // Only sent when it actually changed - no point auditing a no-op
        // as a prefix change.
        ...(prefixChanged ? { taskKeyPrefix: prefix.trim().toUpperCase() } : {}),
      }));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save the organisation');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Edit organisation</h3>
        <span className="font-mono text-xs text-slate-400">{org.id}</span>
      </div>

      {/*
        A real form layout, not a strip of inputs crammed into a table
        cell. Two rows of two on anything wider than a phone: name and
        prefix together (the two identity fields), timezone and locale
        together (the two formatting ones).
      */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name">
          <input required value={name} onChange={(e) => setName(e.target.value)}
                 className={inputClass} />
        </Field>

        <Field
          label="Task key prefix"
          hint={prefixChanged
            ? undefined
            : `Task keys read ${org.taskKeyPrefix}1, ${org.taskKeyPrefix}2, and so on.`}
        >
          <input
            required
            value={prefix}
            maxLength={8}
            onChange={(e) => setPrefix(e.target.value.toUpperCase())}
            placeholder="CG-"
            className={`${inputClass} font-mono ${
              prefixChanged ? 'border-amber-400 bg-amber-50' : ''
            }`}
          />
        </Field>

        <Field label="Timezone" hint="An IANA name, e.g. Asia/Kolkata. Drives due dates and the digest.">
          <input required value={timezone} onChange={(e) => setTimezone(e.target.value)}
                 className={inputClass} />
        </Field>

        <Field label="Locale" hint="e.g. en-IN. Date and number formatting.">
          <input required value={locale} onChange={(e) => setLocale(e.target.value)}
                 className={inputClass} />
        </Field>
      </div>

      {prefixChanged && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
          <p className="font-semibold">
            Changing the prefix is not retroactive.
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
            <li>
              Existing tasks keep <span className="font-mono">{org.taskKeyPrefix}</span> for ever
            </li>
            <li>
              Only new tasks get <span className="font-mono">{prefix.trim().toUpperCase()}</span>,
              and the numbering carries on — you will see{' '}
              <span className="font-mono">{org.taskKeyPrefix}41</span> then{' '}
              <span className="font-mono">{prefix.trim().toUpperCase()}42</span>
            </li>
            <li>Keys already printed on sent PDFs and emails cannot change</li>
            <li>Future CG Sign serial numbers change too</li>
          </ul>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-3">
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={busy} className={buttonClass}>
          {busy ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}
